import React, { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarIcon, Moon, Heart } from 'lucide-react';
import { format, parseISO, isSameDay, addDays, subDays } from 'date-fns';
import { calculateLunarPhase, calculateCycleDay, calculateExpectedPeriodDates, getCyclePhase, LunarPhaseData } from '@/utils/lunarPhases';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import DateDetailsDialog from './DateDetailsDialog';

interface MenstrualCalendarProps {
  onDateSelect?: (date: Date, cycleDay: number, lunarPhase: LunarPhaseData) => void;
  initialCycleStartDate?: Date;
  initialCycleLength?: number;
  periodEndDate?: Date;
  onPeriodEndDateChange?: (date: Date) => void;
  onCycleDataChange?: (startDate: Date, length: number) => void;
  onSaveCycleSettings?: () => void;
  isPerimenopausal?: boolean;
  isMenopausal?: boolean;
}

export default function MenstrualCalendar({ 
  onDateSelect, 
  initialCycleStartDate,
  initialCycleLength = 28,
  periodEndDate,
  onPeriodEndDateChange,
  onCycleDataChange,
  onSaveCycleSettings,
  isPerimenopausal = false,
  isMenopausal = false
}: MenstrualCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [cycleStartDate, setCycleStartDate] = useState<Date>(initialCycleStartDate || new Date());
  const [cycleLength, setCycleLength] = useState<number>(initialCycleLength);
  const [lunarPhases, setLunarPhases] = useState<Map<string, LunarPhaseData>>(new Map());
  const [isSettingStartDate, setIsSettingStartDate] = useState(false);
  const [localPeriodEndDate, setLocalPeriodEndDate] = useState<Date | undefined>(periodEndDate);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState<Date>(new Date());

  // Sync props to local state whenever they change
  useEffect(() => {
    if (initialCycleStartDate) {
      setCycleStartDate(initialCycleStartDate);
    }
  }, [initialCycleStartDate]);

  useEffect(() => {
    if (initialCycleLength) {
      setCycleLength(initialCycleLength);
    }
  }, [initialCycleLength]);

  useEffect(() => {
    setLocalPeriodEndDate(periodEndDate);
  }, [periodEndDate]);

  // Calculate lunar phases dynamically - 2 years back, 4 years forward from today
  useEffect(() => {
    const calculatePhasesForRange = () => {
      const phases = new Map<string, LunarPhaseData>();
      
      // Dynamic range: 2 years before today to 4 years after today
      const today = new Date();
      const startDate = new Date(today.getFullYear() - 2, 0, 1); // 2 years ago
      const endDate = new Date(today.getFullYear() + 4, 11, 31); // 4 years from now
      
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateKey = format(d, 'yyyy-MM-dd');
        // Use consistent UTC noon for all lunar calculations
        const utcDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0));
        const phase = calculateLunarPhase(utcDate);
        phases.set(dateKey, phase);
      }
      
      setLunarPhases(phases);
    };

    calculatePhasesForRange();
  }, []); // Only calculate once on mount

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    
    if (isSettingStartDate) {
      setCycleStartDate(date);
      setIsSettingStartDate(false);
      onCycleDataChange?.(date, cycleLength);
      return;
    }
    
    setSelectedDate(date);
    const cycleDay = calculateCycleDay(cycleStartDate, date);
    const dateKey = format(date, 'yyyy-MM-dd');
    const lunarPhase = lunarPhases.get(dateKey) || calculateLunarPhase(date);
    
    // Open dialog with date details
    setDialogDate(date);
    setDialogOpen(true);
    
    onDateSelect?.(date, cycleDay, lunarPhase);
  };

  const getDayContent = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const lunarPhase = lunarPhases.get(dateKey);
    
    // Calculate cycle day for any date based on cycle start
    let cycleDay = calculateCycleDay(cycleStartDate, date);
    
    // For dates before cycle start, calculate backward from cycle start
    if (date < cycleStartDate) {
      const daysBefore = Math.ceil((cycleStartDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      cycleDay = cycleLength - (daysBefore % cycleLength) + 1;
      if (cycleDay > cycleLength) cycleDay = cycleDay - cycleLength;
    } else {
      // Normalize cycle day to be within 1-cycleLength range for accurate phase calculation
      cycleDay = ((cycleDay - 1) % cycleLength) + 1;
    }
    
    if (cycleDay <= 0) cycleDay = cycleLength + cycleDay;
    
    // Calculate period length from user data if available
    const periodLength = localPeriodEndDate && cycleStartDate 
      ? Math.ceil((localPeriodEndDate.getTime() - cycleStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1
      : 5; // Default to 5 days
    
    const cyclePhase = getCyclePhase(cycleDay, cycleLength, isPerimenopausal, isMenopausal, periodLength);
    const isToday = isSameDay(date, new Date());
    
    // Get phase-specific background colors - hide for menopause, show only lunar
    const getPhaseBackground = (phase: string) => {
      // In menopause mode, don't show cycle phase colors - only lunar
      if (isMenopausal) {
        return 'bg-muted border-border';
      }
      
      switch (phase) {
        case 'menstruation': return 'bg-red-500/20 border-red-500/40'; // ❤️ Red = Menstruation
        case 'follicular': return 'bg-cyan-400/20 border-cyan-400/40'; // 🩵 Cyan = Follicular
        case 'ovulation': return 'bg-pink-500/30 border-pink-500/50'; // 💗 Brighter Pink = Ovulation
        case 'luteal': return 'bg-purple-500/20 border-purple-500/40'; // 💜 Purple = Luteal
        default: return 'bg-muted border-border';
      }
    };
    
    return (
      <div className={cn(
        "w-full h-full flex flex-col items-center justify-center rounded-lg border-2 transition-all hover:scale-105 p-2 min-h-[80px]",
        getPhaseBackground(cyclePhase.phase),
        isToday && "ring-4 ring-primary ring-offset-2"
      )}>
        {/* Lunar phase icon - larger, positioned at top */}
        {lunarPhase && (
          <span className="text-lg font-medium leading-none mb-1 text-primary" title={lunarPhase.name}>
            {lunarPhase.name.split(' ')[0]}
          </span>
        )}
        
        {/* Day number */}
        <span className={cn(
          "text-lg font-semibold text-foreground",
          isToday && "text-primary"
        )}>
          {date.getDate()}
        </span>
      </div>
    );
  };

  const selectedLunarPhase = lunarPhases.get(format(selectedDate, 'yyyy-MM-dd')) || calculateLunarPhase(selectedDate);
  const selectedCycleDay = calculateCycleDay(cycleStartDate, selectedDate);

  return (
    <div className="space-y-6">
      {/* Cycle Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Cycle Settings
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              ✕
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="cycle-start">Last Period Start Date</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="cycle-start"
                  type="date"
                  value={format(cycleStartDate, 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const newDate = parseISO(e.target.value);
                    setCycleStartDate(newDate);
                    onCycleDataChange?.(newDate, cycleLength);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSettingStartDate(true)}
                  className={cn(isSettingStartDate && "bg-primary text-primary-foreground")}
                >
                  {isSettingStartDate ? 'Click Date' : 'Pick'}
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="period-end">Period End Date</Label>
              <Input
                id="period-end"
                type="date"
                value={localPeriodEndDate ? format(localPeriodEndDate, 'yyyy-MM-dd') : ''}
                onChange={(e) => {
                  const date = e.target.value ? parseISO(e.target.value) : undefined;
                  setLocalPeriodEndDate(date);
                  if (date) {
                    onPeriodEndDateChange?.(date);
                  }
                }}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="cycle-length">Cycle Length (days)</Label>
                <Input
                  id="cycle-length"
                  type="number"
                  min="21"
                  max="35"
                  value={cycleLength}
                  onChange={(e) => {
                    const newLength = Number(e.target.value);
                    setCycleLength(newLength);
                    onCycleDataChange?.(cycleStartDate, newLength);
                  }}
                  className="mt-1"
                />
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button 
              onClick={onSaveCycleSettings}
              className="flex items-center gap-2"
            >
              Save Cycle Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card className="bg-gradient-to-br from-background to-muted/20">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
               <Moon className="h-6 w-6 text-primary" />
               <span className="text-xl">Cycle & Lunar Calendar</span>
             </div>
            <div className="text-right text-sm space-y-1">
              <div className="font-semibold text-primary">Day {selectedCycleDay} of cycle</div>
              <div className="text-muted-foreground">{selectedLunarPhase.name}</div>
              <div className="text-xs text-muted-foreground">{selectedLunarPhase.cycleDescription}</div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Modern grid-based calendar */}
          <div className="space-y-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-3">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="text-center text-sm font-semibold text-muted-foreground">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar days as larger grid boxes */}
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              className="w-full pointer-events-auto [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_.rdp-cell]:p-0"
              classNames={{
                months: "w-full",
                month: "w-full space-y-4",
                table: "w-full border-collapse",
                head_row: "hidden", // Hide default header as we have our own
                row: "grid grid-cols-7 gap-3 w-full mb-3",
                cell: "relative p-0",
                day: "w-full h-full p-0",
                day_selected: "bg-transparent",
              }}
              components={{
                Day: ({ date }) => (
                  <button
                    onClick={() => handleDateSelect(date)}
                    className="w-full h-full cursor-pointer"
                  >
                    {getDayContent(date)}
                  </button>
                ),
              }}
            />
          </div>
          
          {/* Cleaner Legend */}
          <div className="border-t pt-6 space-y-4">
            <div className={cn("grid gap-6", isMenopausal ? "grid-cols-1" : "grid-cols-2")}>
              {/* Lunar Phases */}
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Moon className="h-4 w-4 text-primary" />
                  Lunar Phases
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Moon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Waxing (New to Full)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Moon className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">Waning (Full to New)</span>
                  </div>
                  {isMenopausal && (
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      In menopause, attune to the moon's rhythm for continued wisdom and natural cycles.
                    </p>
                  )}
                </div>
              </div>
              
              {/* Cycle Phases - only show if not menopausal */}
              {!isMenopausal && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <Heart className="h-4 w-4 text-primary" />
                    Cycle Phases
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-red-500/40 border border-red-500"></div>
                      <span className="text-muted-foreground">Menstruation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-cyan-400/40 border border-cyan-400"></div>
                      <span className="text-muted-foreground">Follicular</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-pink-500/50 border border-pink-500"></div>
                      <span className="text-muted-foreground">Ovulation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-purple-500/40 border border-purple-500"></div>
                      <span className="text-muted-foreground">Luteal</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Today indicator */}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-2 border-t">
              <div className="w-4 h-4 rounded-full border-4 border-primary"></div>
              <span>Today's date</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Date Details Dialog */}
      <DateDetailsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        date={dialogDate}
        cycleStartDate={cycleStartDate}
        cycleLength={cycleLength}
        lunarPhase={lunarPhases.get(format(dialogDate, 'yyyy-MM-dd')) || calculateLunarPhase(dialogDate)}
        isPerimenopausal={isPerimenopausal}
        isMenopausal={isMenopausal}
      />
    </div>
  );
}